import { useState, useEffect } from 'react';
import mixpanel from 'mixpanel-browser';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { useAuth } from '@/hooks/useAuth';
import { useUserAvatar } from '@/hooks/useUserAvatar';
import { useToast } from '@/hooks/use-toast';
import { useProfileCompletion } from '@/hooks/useProfileCompletion';
import { supabase } from '@/integrations/supabase/client';
import { Camera, User, Phone, Calendar, MapPin, Instagram, Building2, FileText, Save, Loader2 } from 'lucide-react';
import { UploadAvatarDialog } from '@/components/UploadAvatarDialog';

export function UserProfileSettings() {
  const { user } = useAuth();
  const { avatarUrl, refreshAvatar } = useUserAvatar();
  const { toast } = useToast();
  const { removeIncompleteProfileNotification } = useProfileCompletion();
  const [loading, setLoading] = useState(false);
  const [showAvatarDialog, setShowAvatarDialog] = useState(false);
  const [isLoadingProfile, setIsLoadingProfile] = useState(true);
  
  // Estados do formulário
  const [fullName, setFullName] = useState('');
  const [documentType, setDocumentType] = useState<'cpf' | 'cnpj'>('cpf');
  const [cpf, setCpf] = useState('');
  const [cnpj, setCnpj] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [tradeName, setTradeName] = useState('');
  const [phone, setPhone] = useState('');
  const [birthDate, setBirthDate] = useState('');
  const [gender, setGender] = useState<'male' | 'female' | 'other' | 'prefer_not_to_say' | ''>('');
  const [address, setAddress] = useState('');
  const [instagram, setInstagram] = useState('');
  
  // Estados do endereço
  const [zipCode, setZipCode] = useState('');
  const [street, setStreet] = useState('');
  const [addressNumber, setAddressNumber] = useState('');
  const [complement, setComplement] = useState('');
  const [neighborhood, setNeighborhood] = useState('');
  const [city, setCity] = useState('');
  const [state, setState] = useState('');
  const [loadingCep, setLoadingCep] = useState(false);

  // Carregar dados do perfil
  useEffect(() => {
    const loadProfile = async () => {
      setIsLoadingProfile(true);
      
      if (!user?.id) {
        setIsLoadingProfile(false);
        return;
      }

      try {
        const { data: profile, error } = await supabase
          .from('profiles')
          .select('*')
          .eq('id', user.id)
          .maybeSingle();

        if (error) {
          console.error('Erro ao carregar perfil:', error);
          setIsLoadingProfile(false);
          return;
        }

        if (profile) {
          setFullName(profile.name || '');
          setDocumentType((profile.document_type as 'cpf' | 'cnpj') || 'cpf');
          setCpf(profile.cpf || '');
          setCnpj(profile.cnpj || '');
          setCompanyName(profile.company_name || '');
          setTradeName(profile.trade_name || '');
          setPhone(profile.phone || '');
          setBirthDate(profile.birth_date ? profile.birth_date.split('T')[0] : '');
          setGender((profile.gender as any) || '');
          setAddress(profile.address || '');
          setInstagram(profile.instagram || '');
          setZipCode(profile.zip_code || '');
          setStreet(profile.street || '');
          setAddressNumber(profile.address_number || '');
          setComplement(profile.complement || '');
          setNeighborhood(profile.neighborhood || '');
          setCity(profile.city || '');
          setState(profile.state || '');
        }
      } catch (error) {
        console.error('Erro ao carregar perfil:', error);
      } finally {
        setIsLoadingProfile(false);
      }
    };

    loadProfile();
  }, [user?.id]);

  // Função para buscar CEP
  const fetchCep = async (cep: string) => {
    // Remove caracteres não numéricos
    const cleanCep = cep.replace(/\D/g, '');
    
    // Valida se tem 8 dígitos
    if (cleanCep.length !== 8) {
      return;
    }

    setLoadingCep(true);
    try {
      const response = await fetch(`https://viacep.com.br/ws/${cleanCep}/json/`);
      const data = await response.json();

      if (data.erro) {
        toast({
          title: 'CEP não encontrado',
          description: 'O CEP informado não foi encontrado. Verifique e tente novamente.',
          variant: 'destructive',
        });
        return;
      }

      // Preencher campos automaticamente
      setStreet(data.logradouro || '');
      setNeighborhood(data.bairro || '');
      setCity(data.localidade || '');
      setState(data.uf || '');
      
      // Limpar número e complemento para o usuário preencher
      // (não limpar se já tiver valor)
      if (!addressNumber) {
        setAddressNumber('');
      }
      if (!complement) {
        setComplement('');
      }

      toast({
        title: 'CEP encontrado!',
        description: 'Endereço preenchido automaticamente.',
      });
    } catch (error) {
      console.error('Erro ao buscar CEP:', error);
      toast({
        title: 'Erro ao buscar CEP',
        description: 'Não foi possível buscar o endereço. Tente novamente.',
        variant: 'destructive',
      });
    } finally {
      setLoadingCep(false);
    }
  };

  // Handler para mudança de CEP
  const handleZipCodeChange = (value: string) => {
    // Formatar CEP enquanto digita (00000-000)
    const cleanValue = value.replace(/\D/g, '');
    const formatted = cleanValue.length > 5 
      ? cleanValue.replace(/(\d{5})(\d)/, '$1-$2')
      : cleanValue;
    setZipCode(formatted);

    // Buscar CEP quando tiver 8 dígitos
    if (cleanValue.length === 8) {
      fetchCep(cleanValue);
    } else if (cleanValue.length < 8) {
      // Limpar campos se o CEP for alterado e não tiver 8 dígitos
      if (cleanValue.length === 0) {
        setStreet('');
        setNeighborhood('');
        setCity('');
        setState('');
      }
    }
  };

  const handleSave = async () => {
    if (!user?.id) return;
    mixpanel.track('app_profile_saved')
    setLoading(true);
    try {
      // Montar endereço completo para o campo address (compatibilidade)
      const fullAddress = [
        street,
        addressNumber && `Nº ${addressNumber}`,
        complement,
        neighborhood,
        city,
        state && `${state}`,
        zipCode && `CEP: ${zipCode}`
      ].filter(Boolean).join(', ');

      const updateData: any = {
        name: fullName || user.email?.split('@')[0] || 'Usuário',
        email: user.email || null, // Garantir que o email seja sempre salvo
        document_type: documentType,
        phone: phone || null,
        birth_date: birthDate || null,
        gender: gender || null,
        address: fullAddress || address || null,
        instagram: instagram || null,
        zip_code: zipCode || null,
        street: street || null,
        address_number: addressNumber || null,
        complement: complement || null,
        neighborhood: neighborhood || null,
        city: city || null,
        state: state || null,
        updated_at: new Date().toISOString(),
      };

      if (documentType === 'cpf') {
        updateData.cpf = cpf || null;
        updateData.cnpj = null;
        updateData.company_name = null;
        updateData.trade_name = null;
      } else {
        updateData.cnpj = cnpj || null;
        updateData.company_name = companyName || null;
        updateData.trade_name = tradeName || null;
        updateData.cpf = null;
      }

      const { error } = await supabase
        .from('profiles')
        .update(updateData)
        .eq('id', user.id);

      if (error) {
        throw error;
      }

      toast({
        title: 'Perfil atualizado!',
        description: 'Suas informações foram salvas com sucesso.',
      });

      await refreshAvatar();
      
      // Remover notificação de cadastro incompleto se o perfil estiver completo
      const hasName = fullName && fullName.trim().length > 0
      const hasZipCode = zipCode && zipCode.trim().length > 0
      const hasAddressNumber = addressNumber && addressNumber.trim().length > 0
      
      if (hasName && hasZipCode && hasAddressNumber) {
        await removeIncompleteProfileNotification();
        // O realtime subscription vai atualizar o contador automaticamente
      }
    } catch (error: any) {
      console.error('Erro ao salvar perfil:', error);
      toast({
        title: 'Erro',
        description: error.message || 'Não foi possível salvar o perfil. Tente novamente.',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  if (!user) {
    return (
      <div className="space-y-6">
        <Card>
          <CardContent className="flex items-center justify-center py-12">
            <div className="flex flex-col items-center gap-3">
              <p className="text-sm text-muted-foreground">Carregando informações do usuário...</p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (isLoadingProfile) {
    return (
      <div className="space-y-6">
        <Card>
          <CardContent className="flex items-center justify-center py-12">
            <div className="flex flex-col items-center gap-3">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
              <p className="text-sm text-muted-foreground">Carregando perfil...</p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <User className="h-5 w-5" />
            Perfil do Usuário
          </CardTitle>
          <CardDescription>
            Gerencie suas informações pessoais e de contato
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Avatar */}
          <div className="flex items-center gap-4">
            <Avatar className="h-20 w-20" key={avatarUrl}>
              <AvatarImage 
                src={avatarUrl || undefined}
              />
              <AvatarFallback className="bg-primary/10">
                {user?.email?.charAt(0).toUpperCase() || 'U'}
              </AvatarFallback>
            </Avatar>
            <div className="flex flex-col gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => { mixpanel.track('app_profile_avatar_dialog_opened'); setShowAvatarDialog(true) }}
              >
                <Camera className="mr-2 h-4 w-4" />
                Alterar Foto
              </Button>
              <p className="text-xs text-muted-foreground">
                Clique para alterar sua foto de perfil
              </p>
            </div>
          </div>

          {/* Nome Completo */}
          <div className="space-y-2">
            <Label htmlFor="fullName" className="flex items-center gap-2">
              <User className="h-4 w-4" />
              Nome Completo *
            </Label>
            <Input
              id="fullName"
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
              placeholder="Seu nome completo"
              autoComplete="off"
            />
          </div>

          {/* Tipo de Documento */}
          <div className="space-y-2">
            <Label className="flex items-center gap-2">
              <FileText className="h-4 w-4" />
              Tipo de Documento
            </Label>
            <RadioGroup
              value={documentType}
              onValueChange={(value) => setDocumentType(value as 'cpf' | 'cnpj')}
              className="flex gap-6"
            >
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="cpf" id="cpf" />
                <Label htmlFor="cpf" className="cursor-pointer">CPF</Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="cnpj" id="cnpj" />
                <Label htmlFor="cnpj" className="cursor-pointer">CNPJ</Label>
              </div>
            </RadioGroup>
          </div>

          {/* CPF ou CNPJ */}
          {documentType === 'cpf' ? (
            <div className="space-y-2">
              <Label htmlFor="cpf">CPF</Label>
              <Input
                id="cpf"
                value={cpf}
                onChange={(e) => setCpf(e.target.value)}
                placeholder="000.000.000-00"
                maxLength={14}
              />
            </div>
          ) : (
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="cnpj">CNPJ</Label>
                <Input
                  id="cnpj"
                  value={cnpj}
                  onChange={(e) => setCnpj(e.target.value)}
                  placeholder="00.000.000/0000-00"
                  maxLength={18}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="companyName" className="flex items-center gap-2">
                  <Building2 className="h-4 w-4" />
                  Razão Social
                </Label>
                <Input
                  id="companyName"
                  value={companyName}
                  onChange={(e) => setCompanyName(e.target.value)}
                  placeholder="Razão Social da empresa"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="tradeName">Nome Fantasia</Label>
                <Input
                  id="tradeName"
                  value={tradeName}
                  onChange={(e) => setTradeName(e.target.value)}
                  placeholder="Nome Fantasia da empresa"
                />
              </div>
            </div>
          )}

          {/* Telefone */}
          <div className="space-y-2">
            <Label htmlFor="phone" className="flex items-center gap-2">
              <Phone className="h-4 w-4" />
              Telefone
            </Label>
            <Input
              id="phone"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="(00) 00000-0000"
              autoComplete="off"
            />
          </div>

          {/* Data de Nascimento */}
          <div className="space-y-2">
            <Label htmlFor="birthDate" className="flex items-center gap-2">
              <Calendar className="h-4 w-4" />
              Data de Nascimento
            </Label>
            <Input
              id="birthDate"
              type="date"
              value={birthDate}
              onChange={(e) => setBirthDate(e.target.value)}
            />
          </div>

          {/* Gênero */}
          <div className="space-y-2">
            <Label htmlFor="gender">Gênero</Label>
            <Select value={gender} onValueChange={(value: any) => setGender(value)}>
              <SelectTrigger>
                <SelectValue placeholder="Selecione o gênero" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="male">Masculino</SelectItem>
                <SelectItem value="female">Feminino</SelectItem>
                <SelectItem value="other">Outro</SelectItem>
                <SelectItem value="prefer_not_to_say">Prefiro não informar</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Endereço */}
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="zipCode" className="flex items-center gap-2">
                <MapPin className="h-4 w-4" />
                CEP *
              </Label>
              <div className="flex gap-2">
                <Input
                  id="zipCode"
                  value={zipCode}
                  onChange={(e) => handleZipCodeChange(e.target.value)}
                  placeholder="00000-000"
                  maxLength={9}
                  className="flex-1"
                />
                {loadingCep && (
                  <div className="flex items-center px-3">
                    <Loader2 className="h-4 w-4 animate-spin text-primary" />
                  </div>
                )}
              </div>
              <p className="text-xs text-muted-foreground">
                Digite o CEP e o endereço será preenchido automaticamente
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="md:col-span-2 space-y-2">
                <Label htmlFor="street">Logradouro</Label>
                <Input
                  id="street"
                  value={street}
                  onChange={(e) => setStreet(e.target.value)}
                  placeholder="Rua, Avenida, etc"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="addressNumber">Número *</Label>
                <Input
                  id="addressNumber"
                  value={addressNumber}
                  onChange={(e) => setAddressNumber(e.target.value)}
                  placeholder="123"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="complement">Complemento</Label>
              <Input
                id="complement"
                value={complement}
                onChange={(e) => setComplement(e.target.value)}
                placeholder="Apto, Bloco, etc"
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label htmlFor="neighborhood">Bairro</Label>
                <Input
                  id="neighborhood"
                  value={neighborhood}
                  onChange={(e) => setNeighborhood(e.target.value)}
                  placeholder="Bairro"
                />
              </div>
              <div className="md:col-span-2 space-y-2">
                <Label htmlFor="city">Cidade</Label>
                <Input
                  id="city"
                  value={city}
                  onChange={(e) => setCity(e.target.value)}
                  placeholder="Cidade"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="state">Estado (UF)</Label>
              <Input
                id="state"
                value={state}
                onChange={(e) => setState(e.target.value.toUpperCase())}
                placeholder="UF"
                maxLength={2}
                className="w-20"
              />
            </div>
          </div>

          {/* Instagram */}
          <div className="space-y-2">
            <Label htmlFor="instagram" className="flex items-center gap-2">
              <Instagram className="h-4 w-4" />
              Instagram
            </Label>
            <Input
              id="instagram"
              value={instagram}
              onChange={(e) => setInstagram(e.target.value)}
              placeholder="@seuinstagram"
            />
          </div>

          {/* Botão Salvar */}
          <div className="flex justify-end pt-4">
            <Button onClick={handleSave} disabled={loading || !fullName}>
              {loading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Salvando...
                </>
              ) : (
                <>
                  <Save className="mr-2 h-4 w-4" />
                  Salvar Alterações
                </>
              )}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Dialog para upload de avatar */}
      <UploadAvatarDialog
        open={showAvatarDialog}
        onOpenChange={setShowAvatarDialog}
      />
    </div>
  );
}
